import React, { useState, useEffect } from 'react';
import { useObserver } from 'mobx-react-lite';
import {
  Button,
  Card,
  Text,
  Intent,
  Dialog,
  FormGroup,
  TextArea,
  Callout,
  NonIdealState,
  Spinner,
  DialogBody,
  DialogFooter
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { DataTable, DropDown } from '@zaiusinc/hera';

import { AppDetailStore } from '../../../stores/AppDetailStore';
import { ZipApi } from '../../../lib/ZipApi';
import { directoryStore } from '../../../stores/DirectoryStore';
import { showToasts } from '../../../lib/toaster';
import { OpalToolDescriptions } from '../../../types/OpalToolDescriptions';
import styles from './OpalToolDescriptionsEditor.module.sass';

interface Props {
  detailStore: AppDetailStore;
}

interface FunctionOption {
  name: string;
  endpoint: string;
}

export const OpalToolDescriptionsEditor = ({ detailStore }: Props) => {
  const [loading, setLoading] = useState(true);
  const [functions, setFunctions] = useState<FunctionOption[]>([]);
  const [selectedFunction, setSelectedFunction] = useState<string>('');
  const [toolDescriptions, setToolDescriptions] = useState<OpalToolDescriptions[]>([]);
  const [editingTool, setEditingTool] = useState<OpalToolDescriptions | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Helper method to generate parameter error keys
  const paramErrorKey = (paramIndex: number): string => {
    return `parameter_${paramIndex}`;
  };

  useEffect(() => {
    const fetchFunctions = async () => {
      if (!directoryStore.trackerId || !detailStore.installId) {
        return;
      }

      try {
        setLoading(true);
        const functionEndpoints = await ZipApi.listFunctions(detailStore.installId);
        const functionOptions = Object.entries(functionEndpoints).map(([name, endpoint]) => ({
          name,
          endpoint
        }));
        setFunctions(functionOptions);

        // Auto-select first function if available
        if (functionOptions.length > 0 && !selectedFunction) {
          setSelectedFunction(functionOptions[0].name);
        }
      } catch (error) {
        showToasts([{ intent: Intent.DANGER, message: 'Error fetching app functions' }]);
        setLoading(false);
      }
    };

    fetchFunctions();
  }, [directoryStore.trackerId, detailStore.installId]);

  useEffect(() => {
    const fetchToolDescriptions = async () => {
      if (!directoryStore.trackerId || !detailStore.appId || !selectedFunction) {
        return;
      }

      try {
        setLoading(true);
        const response = await ZipApi.getOpalToolDescriptions(
          directoryStore.trackerId,
          detailStore.appId,
          selectedFunction
        );
        setToolDescriptions(response.tools || []);
      } catch (error) {
        showToasts([{ intent: Intent.DANGER, message: 'Error fetching tool descriptions' }]);
        setToolDescriptions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchToolDescriptions();
  }, [directoryStore.trackerId, detailStore.appId, selectedFunction]);

  const handleEditTool = (tool: OpalToolDescriptions) => {
    setEditingTool({
      ...tool,
      parameters: tool.parameters || []
    });
    setIsEditDialogOpen(true);
    setValidationErrors({});
  };

  const validateTool = (tool: OpalToolDescriptions): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!tool.description || tool.description.trim() === '') {
      errors.description = 'Tool description is required';
    }

    // Validate each parameter description
    if (tool.parameters) {
      tool.parameters.forEach((param, index) => {
        if (!param.description || param.description.trim() === '') {
          errors[paramErrorKey(index)] = `Description for parameter "${param.name}" is required`;
        }
      });
    }

    return errors;
  };

  const handleSaveDescriptions = async () => {
    if (!directoryStore.trackerId || !detailStore.appId || !selectedFunction || !editingTool) {
      return;
    }

    // Validate the tool
    const errors = validateTool(editingTool);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      setSaveLoading(true);
      setValidationErrors({});

      // Send only the edited tool, not all tools
      await ZipApi.updateOpalToolDescriptions(
        directoryStore.trackerId,
        detailStore.appId,
        selectedFunction,
        { tools: [editingTool] },
        directoryStore.getOptiAccessToken()
      );

      // Update the local state with the edited tool
      const updatedTools = toolDescriptions.map((tool) =>
        tool.name === editingTool.name ? editingTool : tool
      );
      setToolDescriptions(updatedTools);
      setIsEditDialogOpen(false);
      setEditingTool(null);
      showToasts([
        {
          intent: Intent.SUCCESS,
          message: 'Tool descriptions updated successfully! Please sync the tool to apply changes.'
        }
      ]);
    } catch (error) {
      showToasts([{ intent: Intent.DANGER, message: 'Error updating tool descriptions' }]);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteOverrides = async () => {
    if (!directoryStore.trackerId || !detailStore.appId || !selectedFunction) {
      return;
    }

    try {
      setLoading(true);
      await ZipApi.deleteOpalToolDescriptions(
        directoryStore.trackerId,
        detailStore.appId,
        selectedFunction,
        directoryStore.getOptiAccessToken()
      );

      const response = await ZipApi.getOpalToolDescriptions(
        directoryStore.trackerId,
        detailStore.appId,
        selectedFunction
      );
      setToolDescriptions(response.tools || []);
      showToasts([
        {
          intent: Intent.SUCCESS,
          message: 'Tool description overrides deleted successfully! Please sync the tool to apply changes.'
        }
      ]);
    } catch (error) {
      showToasts([{ intent: Intent.DANGER, message: 'Error deleting tool description overrides' }]);
    } finally {
      setLoading(false);
    }
  };

  const updateEditingToolDescription = (description: string) => {
    if (!editingTool) return;

    setEditingTool({
      ...editingTool,
      description
    });

    // Clear validation error when user starts typing
    if (validationErrors.description && description.trim() !== '') {
      setValidationErrors((prev) => {
        const { description: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const updateEditingParameterDescription = (paramIndex: number, description: string) => {
    if (editingTool) {
      const updatedParameters = [...editingTool.parameters];
      updatedParameters[paramIndex] = { ...updatedParameters[paramIndex], description };
      setEditingTool({ ...editingTool, parameters: updatedParameters });

      // Clear validation error when user starts typing
      const errorKey = paramErrorKey(paramIndex);
      if (validationErrors[errorKey] && description.trim() !== '') {
        setValidationErrors((prev) => {
          const { [errorKey]: _, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  return useObserver(() => (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text className={styles.title}>Mark Tools Descriptions</Text>
        <Text className={styles.description}>
          Manage tool and parameter descriptions for Mark AI integration.
          These descriptions help Mark understand when and how to use your tools.
        </Text>
      </div>

      <Card className={styles.content}>
        <div className={styles.functionSection}>
          <FormGroup label="Function" labelFor="function-select">
            <DropDown
              items={functions.map((func) => ({
                text: func.name,
                value: func.name
              }))}
              value={selectedFunction}
              onItemSelect={(item) => setSelectedFunction(item.value)}
              disabled={loading}
              width={250}
              fill
            />
          </FormGroup>
        </div>

        {selectedFunction && (
          <>
            <div className={styles.toolsHeader}>
              <Text className={styles.sectionTitle}>Tool Descriptions</Text>
              <div className={styles.actions}>
                <Button
                  text="Delete Overrides"
                  intent={Intent.WARNING}
                  icon={IconNames.TRASH}
                  onClick={handleDeleteOverrides}
                  disabled={loading}
                  small
                />
              </div>
            </div>

            {loading ? (
              <div className={styles.loading}>
                <Spinner size={30} />
                <Text>Loading tool descriptions...</Text>
              </div>
            ) : toolDescriptions.length === 0 ? (
              <NonIdealState
                icon={IconNames.SEARCH}
                title="No Tools Found"
                description="No Mark tools found for this function."
              />
            ) : (
              <div className={styles.opalToolsTable}>
                <DataTable
                  columns={[
                  {
                    id: 'toolName',
                    title: 'Tool Name',
                    width: '200px',
                    sortable: false,
                    className: styles.toolName,
                    formatter: (_v: unknown, tool: OpalToolDescriptions) => tool.name,
                  },
                  {
                    id: 'description',
                    title: 'Description',
                    width: '300px',
                    sortable: false,
                    className: styles.tableDescription,
                    formatter: (_v: unknown, tool: OpalToolDescriptions) => (
                      <span title={tool.description}>{tool.description}</span>
                    ),
                  },
                  {
                    id: 'parameters',
                    title: 'Parameters',
                    width: '120px',
                    sortable: false,
                    className: styles.parameters,
                    formatter: (_v: unknown, tool: OpalToolDescriptions) =>
                      `${tool.parameters?.length || 0} parameter${tool.parameters?.length > 1 ? 's' : ''}`,
                  },
                  {
                    id: 'actions',
                    title: 'Actions',
                    width: '100px',
                    sortable: false,
                    formatter: (_v: unknown, tool: OpalToolDescriptions) => (
                      <Button
                        text="Edit"
                        intent={Intent.PRIMARY}
                        icon={IconNames.EDIT}
                        onClick={() => handleEditTool(tool)}
                        small
                      />
                    ),
                  },
                ]}
                data={toolDescriptions}
                loading={false}
                hideTop={true}
                hideBottom={true}
                hideBorder={true}
                rowStart={1}
                rowCount={toolDescriptions.length}
                total={toolDescriptions.length}
                sortColumn=""
                sortAsc={true}
                onPaging={() => { /* unused — small static list, no paging */ }}
                onSorting={() => { /* unused — sortable: false on every column */ }}
              />
              </div>
            )}
          </>
        )}
      </Card>

      <Dialog
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        title={`Edit Tools Descriptions`}
        className={styles.editDialog}
      >
        <DialogBody>
          {editingTool && (
            <>
              <Callout intent={Intent.PRIMARY} className={styles.callout}>
                <Text>
                  Update the tool and parameter descriptions to improve Mark AI's understanding of when and
                  how to use this tool.
                </Text>
              </Callout>

              <div className={styles.toolNameLeftBorder}>
                <Text className={styles.toolNameLabel}>Tool Name:</Text>
                <Text className={styles.toolNameValue}>{editingTool.name}</Text>
              </div>

              <FormGroup
                label="Tool Description"
                labelFor="tool-description"
                helperText={validationErrors.description}
                intent={validationErrors.description ? Intent.DANGER : Intent.NONE}
              >
                <TextArea
                  id="tool-description"
                  value={editingTool.description}
                  onChange={(e) => updateEditingToolDescription(e.target.value)}
                  fill
                  rows={3}
                  placeholder="Describe what this tool does and when it should be used..."
                  intent={validationErrors.description ? Intent.DANGER : Intent.NONE}
                />
              </FormGroup>

              {editingTool.parameters?.length > 0 && <Text className={styles.parametersTitle}>Parameters</Text>}
              <div className={styles.parametersContainer}>
                {editingTool.parameters && editingTool.parameters.map((param, index) => (
                  <div key={param.name} className={styles.parameterItem}>
                    <FormGroup
                      label={
                        <div className={styles.parameterLabel}>
                          <Text className={styles.parameterName}>{param.name}</Text>
                        </div>
                      }
                      helperText={validationErrors[paramErrorKey(index)]}
                      intent={validationErrors[paramErrorKey(index)] ? Intent.DANGER : Intent.NONE}
                    >
                      <TextArea
                        value={param.description}
                        onChange={(e) => updateEditingParameterDescription(index, e.target.value)}
                        fill
                        rows={2}
                        placeholder={`Describe the ${param.name} parameter...`}
                        intent={validationErrors[paramErrorKey(index)] ? Intent.DANGER : Intent.NONE}
                      />
                    </FormGroup>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogBody>
        <DialogFooter actions={
          <>
            <Button
              text="Cancel"
              onClick={() => setIsEditDialogOpen(false)}
              disabled={saveLoading}
            />
            <Button
              text="Save Changes"
              intent={Intent.PRIMARY}
              onClick={handleSaveDescriptions}
              loading={saveLoading}
              disabled={saveLoading}
            />
          </>
        }>
        </DialogFooter>
      </Dialog>
    </div>
  ));
};
